import { getFormProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { Form } from '@remix-run/react'
import { type Dispatch, type SetStateAction, forwardRef } from 'react'
import { type GeneratedImage2 } from '#app/routes/resources+/generated-images'
import { decodeImageFormat, UpscalerSchema } from '#app/utils/helpers'
import { useUpscaleDimensions } from '#app/utils/hooks/useUpscaleDimensions'
import { useUserCredits } from '#app/utils/hooks/useUserCredits'
import { DimensionInputs } from './dimension-inputs'
import { ErrorList } from './forms'
import { Alert, AlertDescription } from './ui/alert'
import { Button } from './ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from './ui/dialog'
import HiddenInput from './ui/hidden-input'
import { Icon } from './ui/icon'
import { UpscalePresets } from './upscale-presets'

const DisabledToggleItem = forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>((props, ref) => (
	<div
		ref={ref}
		{...props}
		className={`cursor-not-allowed px-3 py-2 opacity-50 ${props.className || ''}`}
	/>
))

DisabledToggleItem.displayName = 'DisabledToggleItem'

export function DialogUpscaler({
	open,
	onClose,
	image,
}: {
	open: boolean
	onClose: Dispatch<SetStateAction<boolean>>
	image: GeneratedImage2
}) {
	const {
		upscaleWidth,
		upscaleHeight,
		selectedPreset,
		handlePresetChange,
		handleWidthChange,
		handleHeightChange,
	} = useUpscaleDimensions(image)

	const [form, fields] = useForm({
		id: 'upscaler',
		constraint: getZodConstraint(UpscalerSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: UpscalerSchema })
		},
		shouldRevalidate: 'onInput',
		defaultValue: {
			width: image?.format
				? decodeImageFormat(image?.format)?.resolution?.width * 2
				: 0,
			height: image?.format
				? decodeImageFormat(image?.format)?.resolution?.height * 2
				: 0,
			inputImage: {
				id: image.id,
				ownerId: image.ownerId,
			},
		},
	})
	const [generationCost, hasCredits] = useUserCredits(
		1, // batchSize is always 1 for upscaling
		'upscale',
	)

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent
				className="max-w-md"
				onClick={(e) => e.stopPropagation()}
				forceMount
			>
				<DialogHeader>
					<DialogTitle className="text-2xl font-bold">
						Image Upscaler
					</DialogTitle>
					<DialogDescription className="text-gray-500">
						Enhance your image resolution
					</DialogDescription>
				</DialogHeader>

				<Alert>
					<Icon name="warning-circle" className="h-4 w-4">
						<AlertDescription>
							Enhances resolution without changing content.
						</AlertDescription>
					</Icon>
				</Alert>
				<Alert variant="destructive">
					<Icon name="warning-circle" className="h-4 w-4">
						<AlertDescription>
							Enhances resolution without changing content.
						</AlertDescription>
					</Icon>
				</Alert>
				<Form
					key={image.id}
					method="POST"
					action="/resources/upscaler"
					{...getFormProps(form)}
					onSubmit={(e) => {
						if (!hasCredits) {
							e.preventDefault()
						} else {
							onClose(false)
						}
					}}
					className="mb-2 flex flex-col gap-4"
				>
					<HiddenInput
						field={fields.inputImage}
						value={JSON.stringify({
							id: image.id,
							ownerId: image.ownerId,
						})}
					/>

					<UpscalePresets
						selectedPreset={selectedPreset}
						onPresetChange={handlePresetChange}
						originalDimensions={{
							width: image?.format
								? decodeImageFormat(image?.format)?.resolution?.width
								: 0,
							height: image?.format
								? decodeImageFormat(image?.format)?.resolution?.height
								: 0,
						}}
					/>

					<DimensionInputs
						width={upscaleWidth}
						height={upscaleHeight}
						onWidthChange={handleWidthChange}
						onHeightChange={handleHeightChange}
						fields={fields}
					/>

					<div className="flex flex-col items-end justify-center gap-2">
						<Button
							type="submit"
							disabled={!hasCredits}
							className="mt-4 w-full"
						>
							<div className="flex items-center justify-center space-x-2">
								<p>Upscale Image</p>
								<span className="flex items-center text-sm text-primary-foreground">
									({generationCost}
									<Icon name="coins" className="ml-1 h-4 w-4" />)
								</span>
							</div>
						</Button>

						{!hasCredits ? (
							<div className="w-full">
								<ErrorList
									errors={[
										`Insufficient credits. You need ${generationCost} credits for this upscaling.`,
									]}
									id={form.errorId}
								/>
							</div>
						) : null}
					</div>
				</Form>
			</DialogContent>
		</Dialog>
	)
}
